import { PartySocket } from 'partysocket';
import { readSettings } from './settings';
import { getDayKey } from './daily';
import { showToast } from './toast';

export type CoopPlayer = {
	id: string;
	username: string;
	avatar: string;
	connected: boolean;
	isHost: boolean;
	featuredBadge?: string;
};

export type CoopMove = { playerId: string; payload: unknown; at: number };

export type CoopState = {
	gameType?: string;
	date?: string;
	players: CoopPlayer[];
	turnIndex: number;
	moves: CoopMove[];
	liveInput: { playerId: string; payload: unknown } | null;
	finished: boolean;
	started: boolean;
	solo: boolean;
	readyPlayers: string[];
	startedAt: number;
};

const PARTYKIT_HOST = (import.meta.env.VITE_PARTYKIT_HOST as string | undefined) ?? (import.meta.env.DEV ? 'localhost:1999' : 'bubble-games-coop.loljoshie.partykit.dev');

export function getOrCreateUserId(): string {
	if (typeof window === 'undefined') return 'ssr';
	let id = localStorage.getItem('coop-user-id');
	if (!id) {
		id = crypto.randomUUID?.() ?? `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
		localStorage.setItem('coop-user-id', id);
	}
	return id;
}

/**
 * Per-device secret that proves ownership of the userId to the server.
 * Generated on first use, never sent in URLs or broadcasts. Without it
 * anyone who learns a userId could forge messages on that user's behalf
 * (see partykit/auth.ts verifyOrAdoptToken — TOFU-adopted server-side).
 */
export function getOrCreateUserToken(): string {
	if (typeof window === 'undefined') return 'ssr';
	let t = localStorage.getItem('coop-user-token');
	if (!t) {
		const bytes = new Uint8Array(32);
		crypto.getRandomValues(bytes);
		t = Array.from(bytes)
			.map((b) => b.toString(16).padStart(2, '0'))
			.join('');
		localStorage.setItem('coop-user-token', t);
	}
	return t;
}

export function generateRoomId(): string {
	const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
	let id = '';
	for (let i = 0; i < 6; i += 1) id += alphabet[Math.floor(Math.random() * alphabet.length)];
	return id;
}

const ACTIVE_ROOMS_KEY = 'bubble-games-active-coop';

export type ActiveCoopRoom = { roomId: string; dateKey: string; setAt?: number };
export type ActiveCoopRooms = Partial<Record<string, ActiveCoopRoom>>;

const ACTIVE_ROOM_TTL_MS = 3 * 60 * 60 * 1000; // 3 hours

export function readActiveCoopRooms(): ActiveCoopRooms {
	if (typeof window === 'undefined') return {};
	try {
		const raw = localStorage.getItem(ACTIVE_ROOMS_KEY);
		if (!raw) return {};
		const parsed = JSON.parse(raw);
		return typeof parsed === 'object' && parsed ? (parsed as ActiveCoopRooms) : {};
	} catch {
		return {};
	}
}

function writeActiveCoopRooms(rooms: ActiveCoopRooms) {
	if (typeof window === 'undefined') return;
	localStorage.setItem(ACTIVE_ROOMS_KEY, JSON.stringify(rooms));
}

export function setActiveCoopRoom(gameType: string, roomId: string, dateKey: string) {
	const rooms = readActiveCoopRooms();
	rooms[gameType] = { roomId, dateKey, setAt: Date.now() };
	writeActiveCoopRooms(rooms);
}

export function clearActiveCoopRoom(gameType: string) {
	const rooms = readActiveCoopRooms();
	delete rooms[gameType];
	writeActiveCoopRooms(rooms);
}

/**
 * Drop any active-room entries that are no longer relevant — either from a
 * previous puzzle day, or older than the same-day TTL (an abandoned room
 * the user is unlikely to come back to). Call this on app boot.
 */
export function pruneStaleCoopRooms() {
	if (typeof window === 'undefined') return;
	const rooms = readActiveCoopRooms();
	const today = getDayKey();
	const now = Date.now();
	let changed = false;
	for (const key of Object.keys(rooms)) {
		const room = rooms[key];
		if (!room) continue;
		const tooOld = room.setAt !== undefined && now - room.setAt > ACTIVE_ROOM_TTL_MS;
		const wrongDay = room.dateKey !== today;
		if (wrongDay || tooOld) {
			delete rooms[key];
			changed = true;
		}
	}
	if (changed) writeActiveCoopRooms(rooms);
}

const EMPTY_STATE: CoopState = {
	players: [],
	turnIndex: 0,
	moves: [],
	liveInput: null,
	finished: false,
	started: false,
	solo: false,
	readyPlayers: [],
	startedAt: 0
};

export function createCoop(opts: { roomId: string; gameType: string; date: string }) {
	const userId = getOrCreateUserId();
	let settings = readSettings();

	let state = $state<CoopState>(EMPTY_STATE);
	let connected = $state(false);
	let error = $state<string | null>(null);
	let socket: PartySocket | null = null;

	function sendHello() {
		socket?.send(
			JSON.stringify({
				type: 'hello',
				userId,
				token: getOrCreateUserToken(),
				username: settings.username || 'Player',
				avatar: settings.avatar || '',
				featuredBadge: settings.featuredBadge || '',
				gameType: opts.gameType,
				date: opts.date
			})
		);
	}

	if (typeof window !== 'undefined') {
		setActiveCoopRoom(opts.gameType, opts.roomId, opts.date);

		socket = new PartySocket({
			host: PARTYKIT_HOST,
			room: `${opts.gameType}-${opts.date}-${opts.roomId}`
		});

		let everConnected = false;
		let connectTimeout: ReturnType<typeof setTimeout> | null = setTimeout(() => {
			if (!everConnected) error = 'Can’t reach server.';
		}, 8000);

		socket.addEventListener('open', () => {
			connected = true;
			everConnected = true;
			error = null;
			if (connectTimeout) {
				clearTimeout(connectTimeout);
				connectTimeout = null;
			}
			sendHello();
		});

		socket.addEventListener('error', () => {
			if (!everConnected) error = 'Can’t reach server.';
		});

		// If the user updates their avatar / username while in the room, re-send
		// hello so the server (and every other player) sees the change.
		const onSettingsChange = () => {
			const next = readSettings();
			if (next.avatar !== settings.avatar || next.username !== settings.username || next.featuredBadge !== settings.featuredBadge) {
				settings = next;
				if (connected) sendHello();
			}
		};
		window.addEventListener('storage', onSettingsChange);
		// Stash the listener on the socket so disconnect cleans it up via close handler.
		(socket as unknown as { __onSettingsChange?: () => void }).__onSettingsChange = onSettingsChange;

		socket.addEventListener('close', () => {
			const wasConnected = connected;
			connected = false;
			if (wasConnected && !state.finished) error = 'Lost connection. Reconnecting…';
		});

		// Track each partner's connected flag across state updates so we can
		// toast on disconnect / reconnect transitions (works for N-player rooms).
		const lastConnectedById = new Map<string, boolean>();

		socket.addEventListener('message', (event) => {
			try {
				const msg = JSON.parse(event.data);
				if (msg.type === 'state') {
					const next = msg.state as CoopState;

					for (const p of next.players) {
						if (p.id === userId) continue;
						const prev = lastConnectedById.get(p.id);
						if (prev !== undefined && prev !== p.connected) {
							if (prev && !p.connected) {
								showToast(`${p.username || 'Partner'} disconnected`, 'error');
							} else if (!prev && p.connected) {
								showToast(`${p.username || 'Partner'} rejoined`, 'success');
							}
						}
						lastConnectedById.set(p.id, p.connected);
					}

					state = next;
					// Auto-clear active room once the game has finished
					if (state.finished) clearActiveCoopRoom(opts.gameType);
				} else if (msg.type === 'error') error = msg.message;
			} catch {
				/* ignore */
			}
		});
	}

	function submit(payload: unknown, completed = false, noTurn = false) {
		socket?.send(JSON.stringify({ type: 'move', payload, finished: completed, noTurn }));
	}

	function setLiveInput(payload: unknown) {
		socket?.send(JSON.stringify({ type: 'liveInput', payload }));
	}

	function skip() {
		socket?.send(JSON.stringify({ type: 'skip' }));
	}

	function setReady(ready: boolean) {
		socket?.send(JSON.stringify({ type: 'setReady', ready }));
	}

	function startSolo() {
		socket?.send(JSON.stringify({ type: 'startSolo' }));
	}

	function restart() {
		socket?.send(JSON.stringify({ type: 'restart' }));
	}

	// Nudge the user when they try to act out of turn. Debounced so a flurry of
	// clicks doesn't stack toasts (and re-trigger the toast sound) — one nudge per ~1.5s.
	let lastNotMyTurnAt = 0;
	function notMyTurn() {
		if (state.solo) return;
		const now = Date.now();
		if (now - lastNotMyTurnAt < 1500) return;
		lastNotMyTurnAt = now;
		const active = state.players[state.turnIndex];
		const name = active?.username?.trim();
		showToast(name ? `Hold on, it's ${name}'s turn` : "Hold on, it's not your turn", 'warning');
	}

	function disconnect() {
		const onSettingsChange = (socket as unknown as { __onSettingsChange?: () => void } | null)?.__onSettingsChange;
		if (onSettingsChange) window.removeEventListener('storage', onSettingsChange);
		socket?.close();
		socket = null;
	}

	return {
		get state() {
			return state;
		},
		get connected() {
			return connected;
		},
		get error() {
			return error;
		},
		get userId() {
			return userId;
		},
		get me() {
			return state.players.find((p) => p.id === userId);
		},
		get partner() {
			// First non-me player. Kept for back-compat; for N-player rooms
			// (Word Guess), prefer `partners` to iterate everyone else.
			return state.players.find((p) => p.id !== userId);
		},
		get partners() {
			return state.players.filter((p) => p.id !== userId);
		},
		get activePlayer() {
			return state.players[state.turnIndex];
		},
		get isMyTurn() {
			if (state.solo) return true;
			const a = state.players[state.turnIndex];
			return a ? a.id === userId : false;
		},
		get ready() {
			return state.players.length >= 2;
		},
		submit,
		setLiveInput,
		skip,
		notMyTurn,
		setReady,
		startSolo,
		restart,
		disconnect
	};
}

export type Coop = ReturnType<typeof createCoop>;
